// packages/console/src/api/tenancy.ts
//
// FT-6a tenancy client. Backs the SaaS chrome: identity (UserMenu), workspace
// switcher, plan/usage meter, and API keys. Shapes mirror services/tenancy
// exactly (userView, workspaceView, usageResponse, apiKeyView).

import { servicePaths } from "./config";
import { request } from "./http";

export interface UserView {
  id: string;
  email: string;
  name: string;
}

export interface WorkspaceView {
  id: string;
  name: string;
  planTier: string;
  createdAt: string;
  role?: string;
  active: boolean;
}

export interface MeResponse {
  user: UserView;
  workspace?: WorkspaceView;
}

export interface UsageResponse {
  workspaceId: string;
  planTier: string;
  month: string;
  encodeSecondsUsed: number;
  encodeHoursUsed: number;
  encodeHoursLimit: number;
  storageBytesUsed: number;
  storageBytesLimit: number;
  uploadSessions: number;
  uploadsCompleted: number;
  jobsQueued: number;
  jobsStarted: number;
  jobsCompleted: number;
  jobsFailed: number;
}

export interface ApiKeyView {
  id: string;
  workspaceId: string;
  name: string;
  createdBy: string;
  createdAt: string;
  lastUsedAt?: string;
  revokedAt?: string;
}

// MemberView mirrors services/tenancy memberView. createdBy/createdByName are set for
// accounts provisioned through the console (empty for pre-existing/owner-managed ones).
export interface MemberView {
  userId: string;
  email: string;
  name: string;
  role: string;
  createdBy?: string;
  createdByName?: string;
  createdAt: string;
}

// CollaboratorCredential carries the one-time generated password back to the admin
// who provisioned/reset the account. It is shown ONCE and never retrievable again.
export interface CollaboratorCredential {
  userId: string;
  email: string;
  name: string;
  role: string;
  password: string;
}

export interface SwitchWorkspaceResponse {
  accessToken: string;
  tokenType: string;
  expiresIn?: number;
}

const t = servicePaths.tenancy;

export function getMe(signal?: AbortSignal): Promise<MeResponse> {
  return request<MeResponse>(`${t}/v1/me`, { signal });
}

export function listWorkspaces(signal?: AbortSignal): Promise<{ workspaces: WorkspaceView[] }> {
  return request<{ workspaces: WorkspaceView[] }>(`${t}/v1/workspaces`, { signal });
}

export function getUsage(signal?: AbortSignal): Promise<UsageResponse> {
  return request<UsageResponse>(`${t}/v1/usage`, { signal });
}

export function listApiKeys(signal?: AbortSignal): Promise<{ apiKeys: ApiKeyView[] }> {
  return request<{ apiKeys: ApiKeyView[] }>(`${t}/v1/apikeys`, { signal });
}

export function switchWorkspace(workspaceId: string): Promise<SwitchWorkspaceResponse> {
  return request<SwitchWorkspaceResponse>(`${t}/v1/workspaces/switch`, {
    method: "POST",
    json: { workspaceId },
  });
}

// --- User management (owner + admins; the created_by hierarchy is enforced server-side) ---

export function listMembers(workspaceId: string, signal?: AbortSignal): Promise<{ members: MemberView[] }> {
  return request<{ members: MemberView[] }>(`${t}/v1/workspaces/${encodeURIComponent(workspaceId)}/members`, { signal });
}

// provisionCollaborator creates a NEW password-backed account (member role) under the
// caller. Returns the one-time password to show the admin once.
export function provisionCollaborator(workspaceId: string, email: string, name: string): Promise<CollaboratorCredential> {
  return request<CollaboratorCredential>(`${t}/v1/workspaces/${encodeURIComponent(workspaceId)}/collaborators`, {
    method: "POST",
    json: { email, name },
  });
}

// resetCollaboratorPassword rotates a collaborator's password; returns the new one-time
// password. Admins may reset only accounts they created; the owner may reset anyone.
export function resetCollaboratorPassword(workspaceId: string, userId: string): Promise<CollaboratorCredential> {
  return request<CollaboratorCredential>(
    `${t}/v1/workspaces/${encodeURIComponent(workspaceId)}/collaborators/${encodeURIComponent(userId)}/reset-password`,
    { method: "POST" },
  );
}

// removeMember removes a member from the workspace. Admins may remove only accounts
// they created; the owner may remove anyone (except the last owner).
export function removeMember(workspaceId: string, userId: string): Promise<null> {
  return request<null>(`${t}/v1/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(userId)}`, {
    method: "DELETE",
  });
}

// loginUrl is the OIDC start endpoint the console navigates to on a hard 401.
export function loginUrl(): string {
  return `${t}/v1/auth/login`;
}

// passwordLogin signs a collaborator in with email + password. On success the
// server sets the refresh cookie (credentials: "include"), so the caller then
// boots the session via session.refresh() -- the same path OIDC uses. Failure
// returns a user-facing message (a generic 401 never reveals whether the account
// exists). It does NOT go through request()/the 401 redirect: this IS the login.
export async function passwordLogin(
  email: string,
  password: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  try {
    const res = await fetch(`${t}/v1/auth/password`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (res.ok) return { ok: true };
    if (res.status === 401) return { ok: false, message: "Invalid email or password." };
    if (res.status === 429) return { ok: false, message: "Too many attempts. Please wait and try again." };
    if (res.status === 403) return { ok: false, message: "Your account has no workspace yet. Ask the owner to add you." };
    return { ok: false, message: "Sign-in failed. Please try again." };
  } catch {
    return { ok: false, message: "Network error. Please try again." };
  }
}

export async function logout(): Promise<void> {
  await request<null>(`${t}/v1/auth/logout`, { method: "POST", noAuthRedirect: true });
}
